import { ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import { InputVariant } from "@galaxy-io/dls/inputs/Input";
import PasswordInput from "@galaxy-io/dls/inputs/PasswordInput";
import TextInput from "@galaxy-io/dls/inputs/TextInput";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import type { Integration } from "@gtm/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useRouter } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { DrawerList, DrawerRow } from "@/components/workspace/drawer-content";
import { Inline, Stack } from "@/components/workspace/people-styles";
import { SelectField } from "@/components/workspace/select-field";
import { SourceIcon } from "@/components/workspace/source-icon";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";
import { ConnectionSurface } from "./connection-surface";
import { IntegrationDrawerContent } from "./integration-drawer-content";
import { useConnectionReset } from "./use-connection-reset";

export function WebhookConnection({
  open,
  selected,
  onClose,
  onSaved,
}: {
  open: boolean;
  selected?: Integration;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const [formError, setFormError] = useState<string | undefined>();
  const queryClient = useQueryClient();
  const router = useRouter(),
    { toast } = useToast();
  const config = useQuery<{ bodyFormat: "galaxy" | "attio" }>({
    queryKey: ["webhook-config", selected?.id],
    enabled: open && !!selected,
    queryFn: () =>
      unwrap(
        apiClient().GET("/integrations/webhook/connections/{id}", {
          params: { path: { id: selected!.id } },
        }),
      ),
  });
  const [formatOverride, setFormatOverride] = useState<"galaxy" | "attio" | null>(null);
  const bodyFormat = formatOverride ?? config.data?.bodyFormat ?? "galaxy";
  const loadingConfig = !!selected && !config.data;
  const [name, setName] = useState(selected?.name || "Webhook"),
    [url, setUrl] = useState(""),
    [authorization, setAuthorization] = useState(""),
    [signingSecret, setSigningSecret] = useState(""),
    [clearAuthorization, setClearAuthorization] = useState(false),
    [clearSigningSecret, setClearSigningSecret] = useState(false),
    [busy, setBusy] = useState(false);
  useConnectionReset(open, !!selected, () => {
    setFormError(undefined);
    setName(selected?.name || "Webhook");
    setUrl("");
    setAuthorization("");
    setSigningSecret("");
    setClearAuthorization(false);
    setClearSigningSecret(false);
    setFormatOverride(null);
    setBusy(false);
  });
  async function action<T>(request: () => Promise<T>) {
    setFormError(undefined);
    setBusy(true);
    try {
      const data = await request();
      await queryClient.invalidateQueries({ queryKey: ["integrations"] });
      router.refresh();
      return data;
    } catch (e) {
      setFormError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <ConnectionSurface
      open={open}
      selected={selected}
      details={
        <DrawerList>
          <DrawerRow label="Body format">
            {config.isError
              ? "Could not load configuration"
              : !config.data
                ? "Loading…"
                : config.data.bodyFormat === "attio"
                  ? "Attio person"
                  : "Galaxy event"}
          </DrawerRow>
        </DrawerList>
      }
      flush
      title={selected?.name || "Connect webhook"}
      headerLeading={<SourceIcon source="webhook" size={36} />}
      onClose={onClose}
      busy={busy}
      dirty={
        (formatOverride !== null && formatOverride !== (config.data?.bodyFormat ?? "galaxy")) ||
        !!url ||
        !!authorization ||
        !!signingSecret ||
        clearAuthorization ||
        clearSigningSecret ||
        name !== (selected?.name || "Webhook")
      }
      footer={
        <Button
          label={selected ? "Save connection" : "Connect webhook"}
          isLoading={busy}
          isDisabled={busy || loadingConfig || !name.trim() || (!selected && !url)}
          onClick={async () => {
            const body = {
              name,
              url,
              authorization,
              signingSecret,
              bodyFormat,
              clearAuthorization,
              clearSigningSecret,
            };
            const result = await action(() =>
              selected
                ? unwrap(
                    apiClient().PUT("/integrations/webhook/connections/{id}", {
                      params: { path: { id: selected.id } },
                      body,
                    }),
                  )
                : unwrap(
                    apiClient().POST("/integrations/webhook/connections", {
                      body,
                    }),
                  ),
            );
            if (result) {
              setUrl("");
              setAuthorization("");
              setSigningSecret("");
              setClearAuthorization(false);
              setClearSigningSecret(false);
              toast({
                variant: ToastVariant.SUCCESS,
                header: "Webhook configured",
                description: name.trim(),
                ...toastDefaults(ToastVariant.SUCCESS),
              });
              if (selected) await config.refetch();
              onSaved(result.id);
            }
          }}
        />
      }
    >
      {formError && <Alert variant={AlertVariant.ERROR}>{formError}</Alert>}
      <IntegrationDrawerContent>
        <Stack>
          <TextInput
            ref={(input) => input?.setAttribute("aria-label", "Integration name")}
            label="Integration name"
            value={name}
            onChange={setName}
            variant={InputVariant.BASE}
            fillWidth
            isDisabled={busy}
          />
          {/*
              Plain text, not masked. The endpoint is shown in full on the
              connection card afterwards, so hiding it while it is being typed
              protected nothing and only made a typo impossible to spot — which
              for a delivery destination means silently posting nowhere.
            */}
          <TextInput
            ref={(input) => input?.setAttribute("aria-label", "Endpoint URL")}
            label="Endpoint URL"
            value={url}
            onChange={setUrl}
            placeholder={selected ? "Leave blank to keep the saved endpoint" : "https://…"}
            variant={InputVariant.BASE}
            fillWidth
            isDisabled={busy}
          />
          {config.isError || loadingConfig ? (
            <QueryState
              error={config.error}
              loadingMessage="Loading configuration…"
              onRetry={() => void config.refetch()}
            />
          ) : (
            <SelectField
              label="Body format"
              value={bodyFormat}
              onChange={(value) => setFormatOverride(value as "galaxy" | "attio")}
              disabled={busy}
              options={[
                { value: "galaxy", label: "Galaxy event" },
                { value: "attio", label: "Attio person" },
              ]}
            />
          )}
          <PasswordInput
            ref={(input) => input?.setAttribute("aria-label", "Authorization header (optional)")}
            label="Authorization header (optional)"
            placeholder={selected ? "Leave blank to keep saved authorization" : "Bearer …"}
            value={authorization}
            onChange={setAuthorization}
            fillWidth
            variant={InputVariant.BASE}
            isDisabled={busy}
          />
          <PasswordInput
            ref={(input) => input?.setAttribute("aria-label", "Signing secret (optional)")}
            label="Signing secret (optional)"
            placeholder={selected ? "Leave blank to keep the saved secret" : "HMAC-SHA256 secret"}
            value={signingSecret}
            onChange={setSigningSecret}
            fillWidth
            variant={InputVariant.BASE}
            isDisabled={busy}
          />
          {selected && (
            <Inline>
              <Button
                label={clearAuthorization ? "Keep authorization" : "Remove authorization"}
                variant={ButtonVariant.TERTIARY}
                isDisabled={busy}
                onClick={() => setClearAuthorization(!clearAuthorization)}
              />
              <Button
                label={clearSigningSecret ? "Keep signing secret" : "Remove signing secret"}
                variant={ButtonVariant.TERTIARY}
                isDisabled={busy}
                onClick={() => setClearSigningSecret(!clearSigningSecret)}
              />
            </Inline>
          )}
        </Stack>
      </IntegrationDrawerContent>
    </ConnectionSurface>
  );
}
